import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertCircle,
  BarChart3,
  Bell,
  CheckCircle2,
  FileText,
  Mail,
  MessageSquare,
  Settings,
  ShieldCheck,
  Users,
  XCircle,
  Activity,
  Server,
  Shield,
  Code,
  Zap,
  LineChart,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardCard } from "@/components/email/DashboardCard";

export default function EmailDashboard() {
  // State for real SES notifications and email logs
  const [bounces, setBounces] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to fetch SES notification data and email logs
  const fetchSESData = async () => {
    setLoading(true);

    try {
      // Fetch bounces
      const { data: bounceData, error: bounceError } = await supabase
        .from("email_bounces")
        .select("*")
        .order("timestamp", { ascending: false });

      if (bounceError) {
        console.error("Error fetching bounce data:", bounceError);
      }

      // Fetch complaints
      const { data: complaintData, error: complaintError } = await supabase
        .from("email_complaints")
        .select("*")
        .order("timestamp", { ascending: false });

      if (complaintError) {
        console.error("Error fetching complaint data:", complaintError);
      }

      // Fetch deliveries
      const { data: deliveryData, error: deliveryError } = await supabase
        .from("email_deliveries")
        .select("*")
        .order("timestamp", { ascending: false });

      if (deliveryError) {
        console.error("Error fetching delivery data:", deliveryError);
      }

      // Fetch email logs
      const { data: emailLogData, error: emailLogError } = await supabase
        .from("email_logs")
        .select("*")
        .order("timestamp", { ascending: false });

      if (emailLogError) {
        console.error("Error fetching email log data:", emailLogError);
      }

      setBounces(bounceData || []);
      setComplaints(complaintData || []);
      setDeliveries(deliveryData || []);
      setEmailLogs(emailLogData || []);
    } catch (error) {
      console.error("Error fetching SES data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSESData();
  }, []);

  // Calculate summary metrics from real data
  const totalSent = emailLogs.length;
  const totalDelivered = deliveries.length;
  const totalBounced = bounces.length;
  const totalComplaints = complaints.length;

  // Calculate bounce and complaint rates based on real data
  const bounceRate =
    totalSent > 0 ? ((totalBounced / totalSent) * 100).toFixed(1) : "0.0";
  const complaintRate =
    totalSent > 0 ? ((totalComplaints / totalSent) * 100).toFixed(2) : "0.00";

  // Calculate delivery rate for the dashboard
  const deliveryRate =
    totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : "0.0";
  return (
    <EmailPageLayout
      title="Email Management Dashboard"
      description="Comprehensive AWS SES integration for Numberz Insight"
    >
      <div className="relative bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/20 dark:to-gray-950 py-6 md:py-8 overflow-hidden rounded-lg mb-6 border border-blue-100 dark:border-blue-900/30 shadow-sm">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Email dashboard background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/80 to-white/80 dark:from-blue-950/80 dark:to-gray-950/80"></div>
        </div>
        <div className="relative z-10 px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-2xl md:text-3xl font-bold mb-2 text-blue-800 dark:text-blue-300">
              SES Production Access Demo
            </h1>
            <p className="text-blue-700/80 dark:text-blue-400/80 max-w-2xl mx-auto text-sm md:text-base">
              Demonstration of Numberz Insight's compliance with AWS SES
              production access requirements
            </p>
            <div className="mt-4 inline-flex items-center px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs md:text-sm font-medium border border-yellow-200 dark:border-yellow-800/50 shadow-sm">
              <Server className="h-3.5 w-3.5 mr-1.5" />
              For AWS SES Review Team
            </div>
            <div className="flex items-center justify-center mt-4 gap-4">
              <div className="flex items-center text-xs md:text-sm text-blue-600/70 dark:text-blue-400/70">
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                <span>
                  Last updated:{" "}
                  {new Date().toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                onClick={fetchSESData}
                disabled={loading}
              >
                <RefreshCcw
                  className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`}
                />
                {loading ? "Refreshing..." : "Refresh Data"}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <DashboardCard
            title="SNS Notifications"
            description="Amazon SNS is configured to handle bounces and complaints"
            icon={<Bell className="h-5 w-5 text-green-500" />}
            href="/email/performance"
            count={
              loading ? null : totalBounced + totalComplaints + totalDelivered
            }
            badge="Configured"
            badgeColor="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
          />
        </div>
        <Alert className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 shadow-sm">
          <Server className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <AlertTitle className="text-yellow-800 dark:text-yellow-300 font-bold text-sm md:text-base">
            AWS SES Production Access Request Demo
          </AlertTitle>
          <AlertDescription className="text-yellow-700 dark:text-yellow-400 text-xs md:text-sm">
            This demo dashboard is specifically prepared for the AWS SES team
            reviewing our production access request. It demonstrates our full
            compliance with all AWS SES requirements and best practices for
            production access. We are actively enhancing this dashboard with
            additional features coming soon.
          </AlertDescription>
        </Alert>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 shadow-sm">
          <h3 className="text-yellow-800 dark:text-yellow-300 font-medium flex items-center mb-3 text-sm md:text-base">
            <Server className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            Quick Navigation for AWS SES Review Team
          </h3>
          <p className="text-yellow-700 dark:text-yellow-400 text-xs md:text-sm mb-4">
            Please use these links to quickly navigate to all required
            compliance pages and documentation:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
            <Link
              to="/email/privacy-policy"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <Shield className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Privacy Policy</span>
            </Link>
            <Link
              to="/email/terms"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <FileText className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Terms of Service</span>
            </Link>
            <Link
              to="/email/preferences"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <Settings className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Preferences Center</span>
            </Link>
            <Link
              to="/email/bounce-management"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <Mail className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Bounce Management</span>
            </Link>
            <Link
              to="/email/templates"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <MessageSquare className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Templates</span>
            </Link>
            <Link
              to="/email/monitoring"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <BarChart3 className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Monitoring</span>
            </Link>
            <Link
              to="/email/performance"
              className="flex items-center p-2 md:p-3 bg-yellow-100/50 dark:bg-yellow-900/40 hover:bg-yellow-100 dark:hover:bg-yellow-900/60 rounded-md border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 transition-colors text-xs md:text-sm shadow-sm"
            >
              <LineChart className="h-4 w-4 mr-2 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Email Performance</span>
            </Link>
          </div>
        </div>

        <Tabs defaultValue="metrics" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto flex p-1 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 mb-4 md:mb-6 shadow-sm">
            <TabsTrigger
              value="metrics"
              className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-950 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 text-xs md:text-sm"
            >
              <Activity className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
              Performance Metrics
            </TabsTrigger>
            <TabsTrigger
              value="compliance"
              className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-950 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 text-xs md:text-sm"
            >
              <Shield className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
              Compliance
            </TabsTrigger>
            <TabsTrigger
              value="authentication"
              className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-950 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 text-xs md:text-sm"
            >
              <Code className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
              Authentication
            </TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="space-y-6">
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="border-l-4 border-l-blue-500 bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs sm:text-sm font-medium flex items-center text-gray-900 dark:text-gray-100">
                      <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-blue-500 flex-shrink-0" />
                      Bounce Rate
                    </CardTitle>
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                    >
                      Last 30 days
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 py-2">
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        Loading...
                      </span>
                    </div>
                  ) : (
                    <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {bounceRate}%
                    </div>
                  )}
                  <Progress
                    value={parseFloat(bounceRate) * 10}
                    className="h-1.5 sm:h-2 mt-2 bg-blue-100 dark:bg-blue-900/50"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                    Target: &lt;2% (AWS Requirement)
                  </p>
                </CardContent>
                <CardFooter className="pt-0 px-4 pb-4">
                  <Link to="/email/bounce-management" className="w-full">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 h-8 text-xs w-full justify-center"
                    >
                      <Zap className="h-3.5 w-3.5 flex-shrink-0" />
                      Manage Bounces
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="border-l-4 border-l-amber-500 bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs sm:text-sm font-medium flex items-center text-gray-900 dark:text-gray-100">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-amber-500 flex-shrink-0" />
                      Complaint Rate
                    </CardTitle>
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                    >
                      Last 30 days
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 py-2">
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin text-amber-600 dark:text-amber-400" />
                      <span className="text-sm text-amber-600 dark:text-amber-400">
                        Loading...
                      </span>
                    </div>
                  ) : (
                    <div className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {complaintRate}%
                    </div>
                  )}
                  <Progress
                    value={parseFloat(complaintRate) * 100}
                    className="h-1.5 sm:h-2 mt-2 bg-amber-100 dark:bg-amber-900/50"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                    Target: &lt;0.1% (AWS Requirement)
                  </p>
                </CardContent>
                <CardFooter className="pt-0 px-4 pb-4">
                  <Link to="/email/bounce-management" className="w-full">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/20 h-8 text-xs w-full justify-center"
                    >
                      <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      View Complaints
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="border-l-4 border-l-green-500 bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs sm:text-sm font-medium flex items-center text-gray-900 dark:text-gray-100">
                      <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-green-500 flex-shrink-0" />
                      Delivery Rate
                    </CardTitle>
                    <Badge
                      variant="outline"
                      className="text-[10px] sm:text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                    >
                      Last 30 days
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 py-2">
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin text-green-600 dark:text-green-400" />
                      <span className="text-sm text-green-600 dark:text-green-400">
                        Loading...
                      </span>
                    </div>
                  ) : (
                    <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {deliveryRate}%
                    </div>
                  )}
                  <Progress
                    value={parseFloat(deliveryRate)}
                    className="h-1.5 sm:h-2 mt-2 bg-green-100 dark:bg-green-900/50"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                    Target: &gt;98% (Best Practice)
                  </p>
                </CardContent>
                <CardFooter className="pt-0 px-4 pb-4">
                  <Link to="/email/performance" className="w-full">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 h-8 text-xs w-full justify-center"
                    >
                      <LineChart className="h-3.5 w-3.5 flex-shrink-0" />
                      View Metrics
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="compliance" className="space-y-4 md:space-y-6">
            <h2 className="text-lg md:text-xl font-semibold mb-2 md:mb-4 flex items-center text-gray-900 dark:text-gray-100">
              <Shield className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-500" />
              AWS SES Compliance Components
            </h2>

            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <Mail className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    Bounce Handling
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Comprehensive bounce and complaint processing
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        SNS topic configuration
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Automatic suppression list
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Bounce tracking database
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Complaint monitoring
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/bounce-management" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      Manage Bounces
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    Email Templates
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Compliant email templates with required elements
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        HTML and text versions
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Physical address included
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Unsubscribe mechanism
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Accessibility compliant
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/templates" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      View Templates
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    Compliance Pages
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Required privacy and compliance pages
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Privacy Policy
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Email Preferences Center
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Unsubscribe Page
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Email Terms of Service
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/privacy-policy" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      View Compliance Pages
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <Users className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    List Management
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Comprehensive subscriber and list management
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Double opt-in workflow
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Preference management
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Consent tracking
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        List hygiene automation
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/list-management" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      Manage Lists
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <BarChart3 className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    Monitoring & Metrics
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Comprehensive email performance tracking
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Bounce/complaint tracking
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        CloudWatch alarms
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Engagement metrics
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        A/B testing capabilities
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/monitoring" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      View Metrics
                    </Button>
                  </Link>
                </CardFooter>
              </Card>

              <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-gray-100">
                    <FileText className="h-4 w-4 md:h-5 md:w-5 text-blue-500 flex-shrink-0" />
                    Documentation
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Comprehensive email documentation
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-3">
                  <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Email sending workflow
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Bounce handling procedures
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Consent management process
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Staff training materials
                      </span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter className="pt-0">
                  <Link to="/email/documentation" className="w-full">
                    <Button className="w-full h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700">
                      View Documentation
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>

          <TabsContent
            value="authentication"
            className="space-y-4 md:space-y-6"
          >
            <h2 className="text-lg md:text-xl font-semibold mb-2 md:mb-4 flex items-center text-gray-900 dark:text-gray-100">
              <Code className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-500 flex-shrink-0" />
              Email Authentication
            </h2>

            <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm md:text-base text-gray-900 dark:text-gray-100">
                  Email Authentication Configuration
                </CardTitle>
                <CardDescription className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                  DNS records for proper email authentication
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs md:text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      SPF Record
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto border border-gray-100 dark:border-gray-800">
                      <code className="text-[10px] md:text-xs text-gray-800 dark:text-gray-200">
                        v=spf1 include:amazonses.com ~all
                      </code>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs md:text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      DKIM Records
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto border border-gray-100 dark:border-gray-800">
                      <code className="text-[10px] md:text-xs text-gray-800 dark:text-gray-200">
                        k1._domainkey.numberz.au TXT "v=DKIM1; k=rsa;
                        p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCrLHiExVd55zd/IQ/J/mRwSRMAocV/hMB3jXwaHH36d9NaVynQFYV8NaWi69c1veUtRzGt7yAioXqLj7Z4TeEUoOLgrKsn8YnckGs9i3B3tVFB+Ch/4mPhXWiNfNdynHWBcPcbJ8kjEQ2U8y78dHZj1YeRXXVvWob2OaKynO8/lQIDAQAB;"
                      </code>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs md:text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      DMARC Record
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto border border-gray-100 dark:border-gray-800">
                      <code className="text-[10px] md:text-xs text-gray-800 dark:text-gray-200">
                        _dmarc.numberz.au TXT "v=DMARC1; p=quarantine; pct=100;
                        rua=mailto:dmarc-reports@numberz.au"
                      </code>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs md:text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Custom MAIL FROM Domain
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto border border-gray-100 dark:border-gray-800">
                      <code className="text-[10px] md:text-xs text-gray-800 dark:text-gray-200">
                        mail.numberz.au MX 10
                        feedback-smtp.ap-southeast-2.amazonses.com
                      </code>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8 md:mt-10 pt-4 md:pt-6 border-t border-gray-100 dark:border-gray-800">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-base md:text-lg font-medium mb-2 md:mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <Mail className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-500 flex-shrink-0" />
                Email Management
              </h3>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                Comprehensive email management system with AWS SES integration
                for reliable email delivery.
              </p>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-medium mb-2 md:mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <Shield className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-500 flex-shrink-0" />
                Compliance
              </h3>
              <ul className="space-y-1.5 md:space-y-2">
                <li>
                  <Link
                    to="/email/privacy-policy"
                    className="text-xs md:text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                  >
                    <Shield className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
                    Email Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/email/terms"
                    className="text-xs md:text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                  >
                    <FileText className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
                    Email Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-medium mb-2 md:mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <Settings className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-500 flex-shrink-0" />
                Preferences
              </h3>
              <Link to="/email/preferences">
                <Button
                  variant="default"
                  className="gap-1.5 h-8 text-xs md:text-sm bg-blue-600 hover:bg-blue-700"
                >
                  <Settings className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                  Manage Email Preferences
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100 dark:border-gray-800 text-center">
            <div className="mb-4 p-3 md:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg inline-block mx-auto shadow-sm">
              <p className="text-xs md:text-sm text-yellow-700 dark:text-yellow-300 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
                <span className="flex items-center font-medium">
                  <Server className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
                  Note for AWS SES Review Team:
                </span>
                <span className="sm:ml-2">
                  This demo dashboard showcases our implementation of all
                  required AWS SES production access requirements. We are
                  actively enhancing this dashboard with additional features
                  coming soon. For any questions, please contact{" "}
                  <a
                    href="mailto:aws-ses-review@numberz.au"
                    className="underline font-medium text-yellow-700 dark:text-yellow-300"
                  >
                    aws-ses-review@numberz.au
                  </a>
                </span>
              </p>
            </div>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Last Updated:{" "}
              {new Date().toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>
    </EmailPageLayout>
  );
}
